import { useState, useRef, useEffect } from 'react';
import { Search, Bell, MessageSquare, User, BarChart3, Settings, LogOut, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const LOGO_URL = '/assets/logo.jpg';

const mockNotifications = [
  { id: 1, avatar: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=100&h=100&fit=crop', text: 'Tampa Bay Outreach posted a new update', time: '2m ago', unread: true },
  { id: 2, avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=0', text: 'Your donation of $50 to 3,000 Meals was verified on-chain', time: '1h ago', unread: true },
  { id: 3, avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=5', text: 'Sarah Chen started following you', time: '3h ago', unread: true },
  { id: 4, avatar: 'https://images.unsplash.com/photo-1541544741938-0af808871cc0?w=100&h=100&fit=crop', text: 'Clean Water Champions community has 5 new posts', time: '5h ago', unread: true },
  { id: 5, avatar: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=100&h=100&fit=crop', text: 'Milestone reached: 2,000 of 3,000 meals delivered!', time: '1d ago', unread: true },
];

const mockSuggestions = [
  '3,000 Meals for Tampa Bay', 'Tampa Bay Outreach', 'Clean Water Kigali', 'Youth Code Academy',
  'Station House BBQ Impact', 'Disaster Response', 'Children\'s Cancer Center', 'Solar Clinics',
];

export default function TopBar() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifs, setNotifs] = useState(mockNotifications);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const notifRef = useRef(null);

  const unreadCount = notifs.filter(n => n.unread).length;
  const filtered = searchValue.length > 0
    ? mockSuggestions.filter(s => s.toLowerCase().includes(searchValue.toLowerCase())).slice(0, 5)
    : [];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/explore?q=${encodeURIComponent(searchValue.trim())}`);
      setShowSuggestions(false);
      setSearchValue('');
    }
  };

  const handleSuggestionClick = (s) => {
    navigate(`/explore?q=${encodeURIComponent(s)}`);
    setShowSuggestions(false);
    setSearchValue('');
  };

  const markAllRead = () => setNotifs(ns => ns.map(n => ({ ...n, unread: false })));

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200" data-testid="topbar">
      <div className="flex items-center h-14 px-3 sm:px-4 gap-2 sm:gap-3">
        <Link to="/" className="lg:hidden flex items-center gap-2 shrink-0" data-testid="topbar-logo">
          <img src={LOGO_URL} alt="HumaClub" className="h-7 w-auto rounded" />
        </Link>

        {/* Search — collapsed icon on mobile, full bar on sm+ */}
        <div className="flex-1 max-w-xl mx-auto relative" ref={searchRef}>
          <form onSubmit={handleSearch} className="hidden sm:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => { setSearchValue(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Search projects, NGOs, people..."
              className="w-full h-9 pl-10 pr-4 rounded-lg bg-slate-100 text-sm text-slate-900 placeholder-slate-400 border border-transparent focus:outline-none focus:border-slate-300 focus:bg-white transition-all"
              data-testid="topbar-search"
            />
          </form>
          {/* Mobile search icon that expands */}
          <div className="sm:hidden">
            {mobileSearchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchValue}
                    onChange={(e) => { setSearchValue(e.target.value); setShowSuggestions(true); }}
                    onFocus={() => setShowSuggestions(true)}
                    placeholder="Search..."
                    className="w-full h-9 pl-10 pr-4 rounded-lg bg-slate-100 text-sm text-slate-900 placeholder-slate-400 border border-transparent focus:outline-none focus:border-slate-300 focus:bg-white"
                    data-testid="topbar-search-mobile"
                    autoFocus
                  />
                </div>
                <button type="button" onClick={() => { setMobileSearchOpen(false); setSearchValue(''); setShowSuggestions(false); }} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-slate-100">
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </form>
            ) : (
              <button onClick={() => setMobileSearchOpen(true)} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-slate-100" data-testid="topbar-search-icon">
                <Search className="w-5 h-5 text-slate-500" />
              </button>
            )}
          </div>
          {showSuggestions && filtered.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1" data-testid="search-suggestions">
              {filtered.map((s, i) => (
                <button key={s} onClick={() => handleSuggestionClick(s)} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid={`suggestion-${i}`}>
                  <Search className="w-3.5 h-3.5 text-slate-400" />{s}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {isAuthenticated ? (
            <>
              {/* Notifications */}
              <div className="relative" ref={notifRef}>
                <button onClick={() => setShowNotifs(!showNotifs)} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors relative" data-testid="topbar-notifications">
                  <Bell className="w-5 h-5 text-slate-500" />
                  {unreadCount > 0 && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{unreadCount}</span>}
                </button>
                {showNotifs && (
                  <div className="absolute top-full right-0 mt-2 w-[calc(100vw-24px)] sm:w-80 max-w-sm bg-white border border-slate-200 rounded-xl shadow-xl z-50 -right-2 sm:right-0" data-testid="notifications-panel">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                      <span className="font-semibold text-sm text-slate-900">Notifications</span>
                      <button onClick={markAllRead} className="text-xs text-[#7C3AED] hover:underline" data-testid="mark-all-read">Mark all as read</button>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifs.map(n => (
                        <div key={n.id} className={`flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors ${n.unread ? 'bg-slate-50/50' : ''}`} data-testid={`notif-${n.id}`}>
                          <img src={n.avatar} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-700 leading-snug">{n.text}</p>
                            <p className="text-xs text-slate-400 mt-0.5">{n.time}</p>
                          </div>
                          {n.unread && <div className="w-2 h-2 rounded-full bg-[#7C3AED] shrink-0 mt-2" />}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button className="hidden sm:flex w-9 h-9 rounded-lg items-center justify-center hover:bg-slate-100 transition-colors" data-testid="topbar-messages">
                <MessageSquare className="w-5 h-5 text-slate-500" />
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-8 h-8 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] flex items-center justify-center text-white text-xs font-bold outline-none hover:shadow-md transition-shadow ml-1" data-testid="topbar-avatar">
                    {user?.initials || '?'}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 rounded-xl border-slate-200 shadow-lg mt-1">
                  <div className="px-3 py-2">
                    <p className="font-semibold text-sm text-slate-900 truncate">{user?.name}</p>
                    <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild className="cursor-pointer"><Link to={`/profile/${user?.username}`} className="flex items-center gap-2"><User className="w-4 h-4" /> My Profile</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer"><Link to="/dashboard" className="flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Dashboard</Link></DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer flex items-center gap-2 text-slate-400"><Settings className="w-4 h-4" /> Settings</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate('/welcome'); }} className="cursor-pointer flex items-center gap-2 text-red-500"><LogOut className="w-4 h-4" /> Sign Out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/signin"><Button variant="ghost" size="sm" className="text-sm text-slate-600 rounded-lg" data-testid="topbar-signin">Sign In</Button></Link>
              <Link to="/signup"><Button size="sm" className="btn-primary text-sm rounded-lg px-4" data-testid="topbar-join">Join</Button></Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
